import React, { useEffect, useState } from "react";
import { Section, Eyebrow, Heading, Lede } from "../components/site/Section";
import SubscribeForm from "../components/site/SubscribeForm";
import { SITE } from "../lib/content";
import { getCommunityStats } from "../lib/api";
import { MessageCircle, Twitter, Instagram, Hash, Music2 } from "lucide-react";

const CHANNELS = [
  {
    key: "discord",
    label: "Discord",
    handle: "chainsbeneathbakhtak",
    desc: "Whisper with the cult. Lore drops, art previews, late-night theorycrafting.",
    icon: MessageCircle,
    accent: "#5865F2",
    href: SITE.socials.discord,
  },
  {
    key: "twitter",
    label: "Twitter / X",
    handle: "@Chains_bb",
    desc: "Short omens. Quick releases. The occasional curse.",
    icon: Twitter,
    accent: "#EAE0D5",
    href: SITE.socials.twitter,
  },
  {
    key: "tiktok",
    label: "TikTok",
    handle: "@ChainsbeneathBakhtak",
    desc: "Motion fragments — teasers, character reveals, and behind-the-veil clips.",
    icon: Music2,
    accent: "#FF0050",
    href: SITE.socials.tiktok,
  },
  {
    key: "instagram",
    label: "Instagram",
    desc: "Visual journal of Bakhtak — process, mood, fragments.",
    icon: Instagram,
    accent: "#E1306C",
    href: SITE.socials.instagram,
  },
  {
    key: "reddit",
    label: "Reddit",
    desc: "Long-form lore threads. Pose your theories where they can grow.",
    icon: Hash,
    accent: "#FF4500",
    href: SITE.socials.reddit,
  },
].filter((c) => typeof c.href === "string" && /^https?:\/\//i.test(c.href));

export default function CommunityPage() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let alive = true;
    getCommunityStats()
      .then((d) => alive && setStats(d))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div data-testid="community-page">
      <Section testId="community-hero" className="pt-12 md:pt-24">
        <Eyebrow>Join the Dream</Eyebrow>
        <h1
          data-testid="community-title"
          className="font-display text-4xl sm:text-5xl md:text-7xl text-[#EAE0D5] leading-[1] mt-4 max-w-4xl"
        >
          The chain is heavier
          <br />
          when carried{" "}
          <span className="text-[#C5A059] italic font-light">alone</span>.
        </h1>
        <Lede>
          The Bakhtak community is small, strange, and growing. Subscribe for
          chapter releases, or step into one of our halls below — whichever
          part of the internet you haunt.
        </Lede>
        {stats?.subscribers >= 0 && (
          <p
            className="text-xs tracking-[0.22em] uppercase text-[#C5A059]/80 mt-8"
            data-testid="community-stats"
          >
            {stats.subscribers} dreamer{stats.subscribers === 1 ? "" : "s"}{" "}
            already on the scrolls
          </p>
        )}
      </Section>

      {/* Email capture — primary */}
      <Section testId="community-newsletter" className="pt-0">
        <div className="bg-[#0A0A0A] border border-[#C5A059]/20 p-8 md:p-14 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <Eyebrow>Newsletter</Eyebrow>
            <Heading>The candle list.</Heading>
            <p className="text-[#A39E99] mt-4 leading-relaxed">
              One email per chapter. Sometimes a sketch. Never a sales pitch.
              You can leave the dream any time.
            </p>
          </div>
          <div className="md:col-span-5">
            <SubscribeForm
              source="community"
              testIdPrefix="community-subscribe"
            />
            <p className="text-xs text-[#737373] mt-3">
              Your email is stored only to send Bakhtak updates.
            </p>
          </div>
        </div>
      </Section>

      {/* Channels grid */}
      <Section testId="community-channels" className="pt-0">
        <Eyebrow>Other Halls</Eyebrow>
        <Heading className="mb-10">Where else to find us.</Heading>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
          {CHANNELS.map(({ key, label, handle, desc, icon: Icon, accent, href }) => (
            <a
              key={key}
              href={href}
              target="_blank"
              rel="noreferrer noopener"
              data-testid={`community-channel-${key}`}
              className="group bg-[#0A0A0A] border border-white/5 hover:border-[#C5A059]/30 transition-all duration-500 p-7 flex items-start gap-5"
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = `0 0 50px -25px ${accent}`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "0 0 0 transparent";
              }}
            >
              <div
                className="shrink-0 w-12 h-12 flex items-center justify-center border"
                style={{ borderColor: `${accent}55`, color: accent }}
              >
                <Icon size={20} strokeWidth={1.5} />
              </div>
              <div>
                <div className="font-display text-2xl text-[#EAE0D5]">
                  {label}
                </div>
                {handle && (
                  <div className="text-[11px] tracking-[0.18em] text-[#C5A059] mt-1" data-testid={`community-channel-${key}-handle`}>
                    {handle}
                  </div>
                )}
                <p className="text-sm text-[#A39E99] mt-2 leading-relaxed">
                  {desc}
                </p>
                <span className="link-gold text-xs tracking-[0.25em] uppercase inline-block mt-4">
                  Visit →
                </span>
              </div>
            </a>
          ))}
        </div>
      </Section>
    </div>
  );
}
